import {
  CLEAR_CAPTIONS,
  FETCH_AUTHOR_CAPTION_ALIASES,
  FETCH_CAPTIONS_REVIEW_QUEUE,
  FETCH_CAPTION_VOCABULARY,
  FETCH_SESSION_CAPTIONS,
  FETCH_TRANSLATION_GLOSSARY,
  FETCH_TRANSLATION_INSTRUCTIONS,
  REMOVE_AUTHOR_CAPTION_ALIAS,
  REMOVE_CAPTION_VOCABULARY_TERM,
  REMOVE_SESSION_CAPTION,
  REMOVE_TRANSLATION_GLOSSARY_TERM,
  REMOVE_TRANSLATION_INSTRUCTION,
  SET_CAPTIONS_LOADING,
  UPDATE_SESSION_CAPTION,
  UPSERT_AUTHOR_CAPTION_ALIAS,
  UPSERT_CAPTION_VOCABULARY_TERM,
  UPSERT_TRANSLATION_GLOSSARY_TERM,
  UPSERT_TRANSLATION_INSTRUCTION,
} from "../constants/ActionTypes";

const INITIAL_STATE = {
  reviewQueue: null, // raw paginated payload: { data, page, page_count, total_count, has_more }
  bySession: {}, // { [sessionId]: [caption, ...] }
  vocabulary: null, // [term, ...] — Phase 4.5
  aliasesByAuthor: {}, // { [authorId]: [alias, ...] } — Phase 4.5
  // Translation glossary + instructions — Phase 5. The page filters
  // these arrays in-memory by language; we don't key on language at the
  // reducer level because Kelly will frequently flip between languages
  // and we want to keep both lists hot in cache.
  translationGlossary: null,
  translationInstructions: null,
  isLoading: false,
};

export default function captionsReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case SET_CAPTIONS_LOADING:
      return { ...state, isLoading: !!action.payload };

    case FETCH_CAPTIONS_REVIEW_QUEUE:
      return { ...state, reviewQueue: action.payload };

    case FETCH_SESSION_CAPTIONS:
      return {
        ...state,
        bySession: {
          ...state.bySession,
          [action.payload.sessionId]: action.payload.captions,
        },
      };

    case UPDATE_SESSION_CAPTION: {
      const { sessionId, language, caption } = action.payload;
      const existing = state.bySession[sessionId] ?? [];
      // Phase 6: multiple providers can hold rows for the same
      // (session, language). Disambiguate by both language_code and
      // source so an AssemblyAI update doesn't clobber the Whisper
      // entry (and vice versa). Source on the response payload is the
      // backend's authoritative truth.
      const idx = existing.findIndex(
        c => c.language_code === language && c.source === caption.source
      );
      const next = idx >= 0 ? existing.map((c, i) => (i === idx ? caption : c)) : [...existing, caption];
      return {
        ...state,
        bySession: { ...state.bySession, [sessionId]: next },
      };
    }

    case REMOVE_SESSION_CAPTION: {
      const { sessionId, language, source } = action.payload;
      const existing = state.bySession[sessionId] ?? [];
      // When a specific source is given we drop only that row so the
      // other provider's track stays visible. Falling back to the
      // language-only filter (source undefined) preserves backward-
      // compat with any caller that hasn't been updated yet.
      const predicate = source
        ? c => !(c.language_code === language && c.source === source)
        : c => c.language_code !== language;
      return {
        ...state,
        bySession: {
          ...state.bySession,
          [sessionId]: existing.filter(predicate),
        },
      };
    }

    case FETCH_CAPTION_VOCABULARY:
      return { ...state, vocabulary: action.payload };

    case UPSERT_CAPTION_VOCABULARY_TERM: {
      const term = action.payload;
      const existing = state.vocabulary ?? [];
      const idx = existing.findIndex(t => t.id === term.id);
      const next = idx >= 0 ? existing.map((t, i) => (i === idx ? term : t)) : [...existing, term];
      return { ...state, vocabulary: next };
    }

    case REMOVE_CAPTION_VOCABULARY_TERM:
      return {
        ...state,
        vocabulary: (state.vocabulary ?? []).filter(t => t.id !== action.payload),
      };

    case FETCH_AUTHOR_CAPTION_ALIASES:
      return {
        ...state,
        aliasesByAuthor: {
          ...state.aliasesByAuthor,
          [action.payload.authorId]: action.payload.aliases,
        },
      };

    case UPSERT_AUTHOR_CAPTION_ALIAS: {
      const { authorId, alias } = action.payload;
      const existing = state.aliasesByAuthor[authorId] ?? [];
      const idx = existing.findIndex(a => a.id === alias.id);
      const next = idx >= 0 ? existing.map((a, i) => (i === idx ? alias : a)) : [...existing, alias];
      return {
        ...state,
        aliasesByAuthor: { ...state.aliasesByAuthor, [authorId]: next },
      };
    }

    case REMOVE_AUTHOR_CAPTION_ALIAS: {
      const { authorId, id } = action.payload;
      const existing = state.aliasesByAuthor[authorId] ?? [];
      return {
        ...state,
        aliasesByAuthor: {
          ...state.aliasesByAuthor,
          [authorId]: existing.filter(a => a.id !== id),
        },
      };
    }

    case FETCH_TRANSLATION_GLOSSARY:
      return { ...state, translationGlossary: action.payload };

    case UPSERT_TRANSLATION_GLOSSARY_TERM: {
      const term = action.payload;
      const existing = state.translationGlossary ?? [];
      const idx = existing.findIndex(t => t.id === term.id);
      const next = idx >= 0 ? existing.map((t, i) => (i === idx ? term : t)) : [...existing, term];
      return { ...state, translationGlossary: next };
    }

    case REMOVE_TRANSLATION_GLOSSARY_TERM:
      return {
        ...state,
        translationGlossary: (state.translationGlossary ?? []).filter(t => t.id !== action.payload),
      };

    case FETCH_TRANSLATION_INSTRUCTIONS:
      return { ...state, translationInstructions: action.payload };

    case UPSERT_TRANSLATION_INSTRUCTION: {
      const item = action.payload;
      const existing = state.translationInstructions ?? [];
      const idx = existing.findIndex(t => t.id === item.id);
      const next = idx >= 0 ? existing.map((t, i) => (i === idx ? item : t)) : [...existing, item];
      return { ...state, translationInstructions: next };
    }

    case REMOVE_TRANSLATION_INSTRUCTION:
      return {
        ...state,
        translationInstructions: (state.translationInstructions ?? []).filter(t => t.id !== action.payload),
      };

    case CLEAR_CAPTIONS:
      return INITIAL_STATE;

    default:
      return state;
  }
}
